import React, { useEffect, useRef, useState } from 'react';
import { AxiosProgressEvent } from 'axios';
import { useNavigate } from 'react-router-dom';
// Note: Heroicons imports removed - using text-based icons for now

import { 
  Button, 
  Card,
} from '../components';
import { ExpenseForm, ExpenseFormData } from '../components/forms/ExpenseForm';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../components/auth/AuthProvider';
import { useApiClient } from '../hooks/useApiClient';
import { FileUploadZone } from '../components/forms/FileUploadZone';

// Types
interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

const CreateExpense: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAuthenticated, user } = useAuth();
  const apiClient = useApiClient();

  // State
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusBar, setStatusBar] = useState<'idle' | 'saving' | 'success'>('idle');
  // Local file queue for deferred uploads
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [readingReceipt, setReadingReceipt] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<
    | { filename: string; phase: 'uploading'; progress: number }
    | { filename: string; phase: 'processing' }
    | { filename: string; phase: 'completed' }
    | { filename: string; phase: 'error'; message: string }
    | null
  >(null);
  const processingHintTimer = useRef<NodeJS.Timeout | null>(null);
  const lastTriedFilesRef = useRef<File[] | null>(null);
  // Removed unused documentId state
  const [formData, setFormData] = useState<Partial<ExpenseFormData>>({});
  const [managers, setManagers] = useState<{ id: string; name: string; email?: string }[]>([]);
  // Extracted data preview state
  const [extractedPreview, setExtractedPreview] = useState<{
    extracted: any | null;
    confidence: number;
    applied: boolean;
    selected: { merchant_name: boolean; amount: boolean; currency: boolean; expense_date: boolean; description: boolean };
  } | null>(null);
  const undoFormSnapshot = useRef<Partial<ExpenseFormData> | null>(null);

  // Load categories on component mount
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        // Prefer public endpoint that honors multi-role + approver flags
        const qs = '?roles=manager,finance,admin&include_approvers=true&include_self=false&size=100';
        const mgrs = await apiClient.get<any[]>(`/users/managers${qs}`);
        const transformed = (mgrs || []).map((u: any) => ({ id: u.id, name: u.name || u.email, email: u.email }));
        const filtered = transformed.filter((m: any) => !user || m.id !== user.id);
        setManagers(filtered);
      } catch (e1) {
        console.warn('Public managers endpoint failed; falling back to admin endpoint', e1);
        try {
          const admin = await apiClient.get<any>('/admin/users/?role=manager&size=100');
          const items = Array.isArray(admin?.items) ? admin.items : [];
          const transformed = items
            .filter((u: any) => u?.is_active)
            .map((u: any) => ({ id: u.id, name: u.name || u.email, email: u.email }));
          const filtered = transformed.filter((m: any) => !user || m.id !== user.id);
          setManagers(filtered);
        } catch (e2) {
          console.warn('Managers list not available; manager selector will be hidden', e2);
        }
      }
    };

    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        const data = await apiClient.get<any[]>('/expenses/categories/');
        
        // Transform API data to match CategorySelector expectations
        const transformedCategories = data.map((category: any) => ({
          id: category.id,
          name: category.name,
          description: category.description,
          is_active: category.is_active,
          max_amount: category.max_amount
        }));
        
        setCategories(transformedCategories);
        await fetchManagers();
      } catch (err) {
        console.error('Error loading categories:', err);
        showToast('Failed to load expense categories', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      if (!loadedRef.current) {
        loadedRef.current = true; // guard StrictMode double invoke
        loadInitialData();
      }
      // Also refetch managers when window gains focus (user may have just promoted someone)
      const onFocus = () => {
        fetchManagers().catch(() => void 0);
      };
      window.addEventListener('focus', onFocus);
      return () => window.removeEventListener('focus', onFocus);
    }
  }, [isAuthenticated, apiClient, showToast, user]);

  const loadedRef = useRef(false);

  // Helper: map extracted document data to expense form fields
  const mapExtractedToForm = (extracted: any): Partial<ExpenseFormData> => {
    if (!extracted || typeof extracted !== 'object') return {};
    const mapped: Partial<ExpenseFormData> = {};
    if (extracted.vendor_name) mapped.merchant_name = extracted.vendor_name;
    if (typeof extracted.total_amount === 'number') mapped.amount = extracted.total_amount;
    if (extracted.currency) mapped.currency = extracted.currency;
    if (extracted.transaction_date) mapped.expense_date = extracted.transaction_date;
    // description: best-effort from vendor_name/date
    if (!formData.description && (extracted.vendor_name || extracted.transaction_date)) {
      mapped.description = `${extracted.vendor_name || 'Receipt'}${extracted.transaction_date ? ` - ${extracted.transaction_date}` : ''}`;
    }
    return mapped;
  };

  // When user selects files, immediately upload and try extraction for autofill
  const doUpload = async (files: File[], skipProcessing: boolean) => {
    setSelectedFiles(files);
    if (!files || files.length === 0) return;
    setReadingReceipt(true);
    try {
      const newReceiptUrls: string[] = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        if (skipProcessing) {
          fd.append('skip_processing', 'true');
        }
        // POST /documents/upload (multipart). Provide progress + higher timeout.
        setUploadStatus({ filename: file.name, phase: 'uploading', progress: 0 });
        const resp = await apiClient.postMultipart<any>(
          '/documents/upload',
          fd,
          {
            onUploadProgress: (evt: AxiosProgressEvent) => {
              const total = typeof evt.total === 'number' ? evt.total : undefined;
              const loaded = typeof evt.loaded === 'number' ? evt.loaded : undefined;
              if (total && loaded) {
                const pct = Math.round((loaded / total) * 100);
                setUploadStatus({ filename: file.name, phase: 'uploading', progress: Math.min(99, pct) });
              }
            },
          }
        );
        setUploadStatus({ filename: file.name, phase: 'processing' });
        // After 8s, if still processing, give user a hint
        if (processingHintTimer.current) clearTimeout(processingHintTimer.current);
        processingHintTimer.current = setTimeout(() => {
          if (uploadStatus && uploadStatus.phase === 'processing') {
            showToast('Still processing the receipt… this can take a bit for large PDFs.', 'warning');
          }
        }, 8000);
        // Show extracted data review panel (user chooses to apply or ignore)
        if (!skipProcessing) {
          const confidence: number = Number(resp?.extraction_confidence || 0);
          const extracted = resp?.extracted_data || {};
          const extractionError: string | undefined = resp?.extraction_error || resp?.message;
          if (extracted && Object.keys(extracted).length > 0) {
            setExtractedPreview({
              extracted,
              confidence,
              applied: false,
              selected: {
                merchant_name: Boolean(extracted.vendor_name),
                amount: typeof extracted.total_amount === 'number',
                currency: Boolean(extracted.currency),
                expense_date: Boolean(extracted.transaction_date),
                description: Boolean(extracted.vendor_name || extracted.transaction_date),
              },
            });
            // Informational toast
            showToast('Receipt read. Review and apply extracted fields below.', 'success');
          } else if (resp) {
            // Clear, user-friendly message when auto-extraction fails
            const msg = extractionError
              ? `Receipt uploaded, but auto-read failed: ${extractionError}. You can continue filling the form manually.`
              : 'Receipt uploaded. Auto-read was not possible for this file. You can continue filling the form manually.';
            showToast(msg, 'warning');
          }
        }
        if (resp?.presigned_url) {
          newReceiptUrls.push(resp.presigned_url);
        }
        setUploadStatus({ filename: file.name, phase: 'completed' });
      }
      if (newReceiptUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          receipt_urls: Array.from(new Set([...(prev.receipt_urls || []), ...newReceiptUrls])),
        }));
      }
      // Clear files to avoid re-upload after expense creation
      setSelectedFiles([]);
    } catch (e: any) {
      // Robust error mapping for user-friendly guidance
      const status: number | undefined = e?.response?.status;
      const detail: string | undefined = e?.response?.data?.detail || e?.response?.data?.message || e?.response?.data?.error?.message;
      const correlationId: string | undefined = e?.response?.data?.correlation_id || e?.response?.headers?.['x-correlation-id'];

      // Log full diagnostic info (hidden from users)
      console.error('Receipt upload/extraction failed:', {
        status,
        detail,
        correlationId,
        raw: e,
      });

      let userMessage = 'Failed to upload or read the receipt.';
      if (status === 400) {
        if (detail?.toLowerCase().includes('invalid document association') || detail?.toLowerCase().includes('expense')) {
          userMessage = "We couldn't link this receipt to an expense. Open the expense and upload from its details page, or refresh and try again.";
        } else if (detail?.toLowerCase().includes('invalid') && detail?.toLowerCase().includes('uuid')) {
          userMessage = 'The expense link looks invalid. Please re-open the expense and retry.';
        } else {
          userMessage = detail || 'Invalid request. Please check the file and try again.';
        }
      } else if (status === 404) {
        userMessage = 'The referenced expense was not found. Please refresh and try again.';
      } else if (status === 409) {
        userMessage = 'Your profile is syncing. Please try again in a moment or sign out and back in.';
      } else if (status === 413) {
        userMessage = 'The file is too large. Maximum allowed size is 10 MB.';
      } else if (status === 415) {
        userMessage = 'Unsupported file type. Please upload a PDF or image (JPG/PNG).';
      } else if (status === 429) {
        userMessage = 'Too many uploads in a short time. Please wait a few seconds and try again.';
      } else if (status && status >= 500) {
        userMessage = 'Upload failed due to a server issue. Please try again shortly.';
      } else if (typeof detail === 'string' && detail.length > 0) {
        userMessage = detail;
      } else if (typeof e?.message === 'string' && e.message.length > 0) {
        userMessage = e.message;
      }

      setUploadStatus({ filename: selectedFiles[0]?.name || 'receipt', phase: 'error', message: userMessage });
      showToast(`${userMessage} You can still fill the form manually.`, 'error');
    } finally {
      setReadingReceipt(false);
      if (processingHintTimer.current) clearTimeout(processingHintTimer.current);
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    lastTriedFilesRef.current = files;
    await doUpload(files, false);
  };

  // Handle form submission
  const handleSubmit = async (expenseData: ExpenseFormData, action: 'draft' | 'submit') => {
    try {
      setStatusBar('saving');
      setSubmitting(true);

      // 1) Create expense WITHOUT any receipt URLs first
      const createdExpense = await apiClient.post('/expenses/', {
        category_id: expenseData.category_id,
        amount: expenseData.amount,
        currency: expenseData.currency,
        description: expenseData.description,
        expense_date: expenseData.expense_date,
        merchant_name: expenseData.merchant_name,
        business_purpose: expenseData.business_purpose,
        is_billable: expenseData.is_billable,
        project_code: expenseData.project_code,
        manager_id: expenseData.manager_id || undefined,
      });

      // 2) If files are selected, upload them and associate to the created expense using /documents/upload
      const expenseId = createdExpense?.id;
      let uploadFailures = 0;
      // Use selectedFiles if present; otherwise fall back to the last files the user tried to upload (they may have been cleared after pre-read)
      const filesToAttach = (selectedFiles && selectedFiles.length > 0)
        ? selectedFiles
        : (lastTriedFilesRef.current || []);
      if (expenseId && filesToAttach.length > 0) {
        for (const file of filesToAttach) {
          try {
            // Associate document to expense without processing (fast attach)
            await (async () => {
              const form = new FormData();
              form.append('file', file);
              form.append('expense_id', expenseId);
              form.append('skip_processing', 'true');
              await apiClient.postMultipart('/documents/upload', form);
            })();
          } catch (e) {
            console.error('Failed to upload receipt file:', file.name, e);
            uploadFailures += 1;
          }
        }
      }

      // 3) If action is 'submit', submit for approval
      if (expenseId && action === 'submit') {
        try {
          await apiClient.post(`/expenses/${expenseId}/submit`, {});
        } catch (e) {
          console.error('Failed to submit expense for approval:', e);
          showToast('Expense created, but failed to submit for approval.', 'error');
        }
      }

      // 4) User notifications
      if (action === 'submit') {
        if (uploadFailures === 0) {
          showToast('Expense created and submitted for approval successfully!', 'success');
        } else if (uploadFailures < selectedFiles.length) {
          showToast(`Expense submitted. Some receipts failed to upload (${uploadFailures}/${selectedFiles.length}). You can retry from the expense details.`, 'warning');
        } else {
          showToast('Expense submitted. Receipt uploads failed; you can upload from the expense details.', 'warning');
        }
      } else {
        if (uploadFailures === 0) {
          showToast('Expense saved as draft successfully!', 'success');
        } else if (uploadFailures < selectedFiles.length) {
          showToast(`Draft saved. Some receipts failed to upload (${uploadFailures}/${selectedFiles.length}).`, 'warning');
        } else {
          showToast('Draft saved. Receipt uploads failed; you can upload later.', 'warning');
        }
      }

      // Success status bar then navigate
      setStatusBar('success');
      setTimeout(() => navigate('/expenses'), 900);
    } catch (err: any) {
      const status: number | undefined = err?.response?.status as number | undefined;
      const detail: string | undefined = err?.response?.data?.detail || err?.response?.data?.message || err?.response?.data?.error?.message;
      const correlationId: string | undefined = err?.response?.data?.correlation_id || err?.response?.headers?.['x-correlation-id'];

      console.error('Error creating expense:', {
        status,
        detail,
        correlationId,
        raw: err,
      });

      let userMessage = 'Failed to create expense.';
      if (status === 400) {
        // Known validation: self as manager
        if (detail && detail.toLowerCase().includes('assign yourself as your manager')) {
          userMessage = 'You cannot assign yourself as your manager. Please choose a different manager or leave it blank.';
          setFormData(prev => ({ ...prev, manager_id: null }));
        } else if (detail) {
          userMessage = detail;
        } else {
          userMessage = 'Invalid request. Please review the form and try again.';
        }
      } else if (status === 403) {
        userMessage = detail || 'You do not have permission to perform this action.';
      } else if (status === 404) {
        userMessage = detail || 'Related resource was not found. Please refresh and try again.';
      } else if (typeof status === 'number' && status >= 500) {
        userMessage = 'Server error while creating the expense. Please try again in a moment.';
      } else if (detail) {
        userMessage = detail;
      }

      showToast(userMessage, 'error');
      setStatusBar('idle');
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Sticky Status Bar */}
      {statusBar !== 'idle' && (
        <div className="sticky top-0 z-30">
          {statusBar === 'saving' && (
            <div className="w-full bg-blue-50 text-blue-800 border-b border-blue-200 py-2 px-4 flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              <span className="text-sm font-medium">Saving…</span>
            </div>
          )}
          {statusBar === 'success' && (
            <div className="w-full bg-green-50 text-green-800 border-b border-green-200 py-2 px-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414l2.293 2.293 6.543-6.543a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">Saved successfully</span>
            </div>
          )}
        </div>
      )}
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 -m-4 sm:-m-6 lg:-m-8 mb-6 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Create New Expense</h1>
            <p className="text-sm text-gray-600 mt-1">Add a new expense with receipt and details</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/expenses')}
            className="flex items-center gap-2"
          >
            ←
            Back to Expenses
          </Button>
        </div>
      </div>

        {/* Create Expense Form */}
        <Card className="p-6">
          <div className="max-w-4xl mx-auto">
            {/* Help Text - moved above upload to be visible before users act */}
            <Card className="p-6 bg-blue-50 border-blue-200 mb-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-medium">?</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">
                    Tips for Creating Expenses
                  </h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Upload clear photos of receipts for faster processing</li>
                    <li>• Include detailed descriptions for business purposes</li>
                    <li>• Some categories require receipts before submission</li>
                    <li>• You can save as draft and complete later</li>
                    <li>• Expenses over certain amounts may require additional approval</li>
                  </ul>
                </div>
              </div>
            </Card>
            {/* Upload/Processing Status Banner */}
            {uploadStatus && (
              <div className="mb-4 p-3 rounded-md border flex items-center justify-between "
                   style={{ background: '#f8fafc', borderColor: '#e5e7eb' }}>
                <div className="text-sm">
                  {uploadStatus.phase === 'uploading' && (
                    <span>
                      Uploading <strong>{uploadStatus.filename}</strong>… {('progress' in uploadStatus) ? `${uploadStatus.progress}%` : ''}
                    </span>
                  )}
                  {uploadStatus.phase === 'processing' && (
                    <span>
                      Processing <strong>{uploadStatus.filename}</strong>… extracting details
                    </span>
                  )}
                  {uploadStatus.phase === 'completed' && (
                    <span>
                      ✓ {uploadStatus.filename} uploaded
                    </span>
                  )}
                  {uploadStatus.phase === 'error' && (
                    <span className="text-error-600">
                      ✗ Failed: {uploadStatus.message}
                    </span>
                  )}
                </div>
                {uploadStatus.phase === 'error' && (
                  <div className="flex items-center gap-3">
                    <button
                      className="text-sm text-primary-700 hover:underline"
                      onClick={() => {
                        const files = lastTriedFilesRef.current || selectedFiles;
                        if (files && files.length > 0) {
                          doUpload(files, false);
                        }
                      }}
                    >
                      Retry read
                    </button>
                    <button
                      className="text-sm text-teal-700 hover:underline"
                      onClick={() => {
                        const files = lastTriedFilesRef.current || selectedFiles;
                        if (files && files.length > 0) {
                          doUpload(files, true);
                        }
                      }}
                    >
                      Retry without reading
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Authentication Status Debug Info */}
            {!isAuthenticated && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  ⚠️ Not authenticated. Please log in to create expenses.
                </p>
              </div>
            )}
            
            {/* Document Upload Section (deferred until after expense create) */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Attach Receipts (Optional)
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Receipts upload immediately to auto-fill form fields. You can still edit details manually.
              </p>
              <FileUploadZone
                onFilesSelected={handleFilesSelected}
                acceptedFileTypes={["image/*", "application/pdf"]}
                maxFileSize={10 * 1024 * 1024}
                maxFiles={5}
                className="bg-gray-50 rounded-lg p-4"
                disabled={submitting || readingReceipt}
              />
            </div>

            {/* Extracted Data Review Panel */}
            {extractedPreview && extractedPreview.extracted && (
              <Card className="p-4 mb-6 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-md font-semibold text-gray-900">Receipt Data Extracted</h3>
                    <p className="text-xs text-gray-600">Select which fields to apply to the form. You can undo after applying.</p>
                  </div>
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{
                      background: extractedPreview.confidence >= 70 ? '#ecfdf5' : '#fff7ed',
                      color: extractedPreview.confidence >= 70 ? '#065f46' : '#9a3412',
                      border: '1px solid',
                      borderColor: extractedPreview.confidence >= 70 ? '#a7f3d0' : '#fed7aa',
                    }}
                  >
                    Confidence: {Math.round(extractedPreview.confidence)}%
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={extractedPreview.selected.merchant_name}
                      onChange={(e) => setExtractedPreview(prev => prev && ({ ...prev, selected: { ...prev.selected, merchant_name: e.target.checked } }))} />
                    <span>
                      Merchant Name: <strong>{extractedPreview.extracted.vendor_name || '—'}</strong>
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={extractedPreview.selected.amount}
                      onChange={(e) => setExtractedPreview(prev => prev && ({ ...prev, selected: { ...prev.selected, amount: e.target.checked } }))} />
                    <span>
                      Amount: <strong>{typeof extractedPreview.extracted.total_amount === 'number' ? extractedPreview.extracted.total_amount : '—'}</strong>
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={extractedPreview.selected.currency}
                      onChange={(e) => setExtractedPreview(prev => prev && ({ ...prev, selected: { ...prev.selected, currency: e.target.checked } }))} />
                    <span>
                      Currency: <strong>{extractedPreview.extracted.currency || '—'}</strong>
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={extractedPreview.selected.expense_date}
                      onChange={(e) => setExtractedPreview(prev => prev && ({ ...prev, selected: { ...prev.selected, expense_date: e.target.checked } }))} />
                    <span>
                      Date: <strong>{extractedPreview.extracted.transaction_date || '—'}</strong>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 sm:col-span-2">
                    <input type="checkbox" checked={extractedPreview.selected.description}
                      onChange={(e) => setExtractedPreview(prev => prev && ({ ...prev, selected: { ...prev.selected, description: e.target.checked } }))} />
                    <span className="truncate">
                      Suggested Description: <strong className="truncate">{`${extractedPreview.extracted.vendor_name || 'Receipt'}${extractedPreview.extracted.transaction_date ? ` - ${extractedPreview.extracted.transaction_date}` : ''}`}</strong>
                    </span>
                  </label>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  {!extractedPreview.applied ? (
                    <>
                      <Button
                        onClick={() => {
                          // Take snapshot for undo
                          undoFormSnapshot.current = { ...formData };
                          const patchAll = mapExtractedToForm(extractedPreview.extracted);
                          const patch: Partial<ExpenseFormData> = {};
                          if (extractedPreview.selected.merchant_name && patchAll.merchant_name) patch.merchant_name = patchAll.merchant_name;
                          if (extractedPreview.selected.amount && typeof patchAll.amount === 'number') patch.amount = patchAll.amount;
                          if (extractedPreview.selected.currency && patchAll.currency) patch.currency = patchAll.currency;
                          if (extractedPreview.selected.expense_date && patchAll.expense_date) patch.expense_date = patchAll.expense_date;
                          if (extractedPreview.selected.description && patchAll.description) patch.description = patchAll.description;
                          if (Object.keys(patch).length > 0) {
                            setFormData(prev => ({ ...prev, ...patch }));
                            setExtractedPreview(prev => prev && ({ ...prev, applied: true }));
                            showToast('Applied extracted fields to the form.', 'success');
                          } else {
                            showToast('No fields selected to apply.', 'warning');
                          }
                        }}
                      >
                        Apply Selected
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setExtractedPreview(null);
                        }}
                      >
                        Ignore
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Undo to previous form snapshot
                          if (undoFormSnapshot.current) {
                            setFormData(undoFormSnapshot.current);
                            undoFormSnapshot.current = null;
                          }
                          setExtractedPreview(null);
                          showToast('Autofill changes have been undone.', 'success');
                        }}
                      >
                        Undo
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            )}

            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Expense Information
              </h2>
              <p className="text-sm text-gray-600">
                Fill in the expense details below. Fields marked with * are required.
              </p>
            </div>

            {/* Managers empty notice for better UX */}
            {!loading && Array.isArray(managers) && managers.length === 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  No managers are currently configured. You can still create your expense, but approval routing may be delayed.
                  Please contact your Admin to add or enable manager approvers.
                </p>
              </div>
            )}

            <ExpenseForm
              categories={categories}
              loading={loading}
              submitting={submitting}
              onSubmit={handleSubmit}
              submitButtonText="Create Expense"
              showSaveAsDraft={true}
              showSubmitForApproval={true}
              initialData={formData}
              managers={managers}
              currentUserId={user?.id}
              currentUserEmail={user?.email}
              onRefreshManagers={async () => {
                try {
                  setLoading(true);
                  // Use the same preference as fetchManagers
                  try {
                    const qs = '?roles=manager,finance,admin&include_approvers=true&include_self=false&size=100';
                    const mgrs = await apiClient.get<any[]>(`/users/managers${qs}`);
                    const transformed = (mgrs || []).map((u: any) => ({ id: u.id, name: u.name || u.email, email: u.email }));
                    const filtered = transformed.filter((m: any) => !user || m.id !== user.id);
                    setManagers(filtered);
                    showToast('Managers list refreshed', 'success');
                  } catch (e1) {
                    const admin = await apiClient.get<any>('/admin/users/?role=manager&size=100');
                    const items = Array.isArray(admin?.items) ? admin.items : [];
                    const transformed = items
                      .filter((u: any) => u?.is_active)
                      .map((u: any) => ({ id: u.id, name: u.name || u.email, email: u.email }));
                    const filtered = transformed.filter((m: any) => !user || m.id !== user.id);
                    setManagers(filtered);
                    showToast('Managers list refreshed', 'success');
                  }
                } catch (e) {
                  showToast('Failed to refresh managers list', 'error');
                } finally {
                  setLoading(false);
                }
              }}
            />
          </div>
        </Card>
    </div>
  );
};

export default CreateExpense;
